import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FileText, Plus, Search, Filter, AlertTriangle,
  Clock, Calendar, Building2, ChevronDown, Download
} from 'lucide-react';
import api, { downloadBlob } from '../services/api';
import { getDateLocale } from '../utils/i18nHelpers';
import './DocumentList.css';

function DocumentList() {
  const navigate = useNavigate();
  const { t } = useTranslation(['documents', 'common']);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    document_type: '',
    status: '',
    authority: '',
    priority: '',
    responsible_department: '',
    search: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  // Справочники для фильтров
  const [docTypes, setDocTypes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [authorities, setAuthorities] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [departments, setDepartments] = useState([]);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const leadershipRoles = ['admin', 'director', 'deputy_director'];
  const isAdmin = user.is_staff || user.is_superuser || leadershipRoles.includes(user.role);

  useEffect(() => {
    fetchReferences();
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReferences = async () => {
    try {
      const [typesRes, statusesRes, authRes, prioRes, deptRes] = await Promise.all([
        api.getDocumentTypes(),
        api.getDocumentStatuses(),
        api.getAuthorities(),
        api.getPriorities(),
        api.getDepartments(),
      ]);
      setDocTypes(typesRes.data.results || typesRes.data);
      setStatuses(statusesRes.data.results || statusesRes.data);
      setAuthorities(authRes.data.results || authRes.data);
      setPriorities(prioRes.data.results || prioRes.data);
      setDepartments(deptRes.data.results || deptRes.data);
    } catch (err) {
      console.error('Ошибка загрузки справочников:', err);
    }
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.document_type) params.document_type = filters.document_type;
      if (filters.status) params.status = filters.status;
      if (filters.authority) params.authority = filters.authority;
      if (filters.priority) params.priority = filters.priority;
      if (filters.responsible_department) params.responsible_department = filters.responsible_department;
      if (filters.search) params.search = filters.search;

      const res = await api.getDocuments(params);
      setDocuments(res.data.results || res.data);
      setLoading(false);
    } catch (err) {
      console.error('Ошибка загрузки документов:', err);
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchDocuments();
  };

  const resetFilters = () => {
    setFilters({
      document_type: '',
      status: '',
      authority: '',
      priority: '',
      responsible_department: '',
      search: '',
    });
    setTimeout(() => fetchDocuments(), 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(getDateLocale());
  };

  const getDeadlineClass = (doc) => {
    if (!doc.deadline) return '';
    if (doc.is_overdue) return 'deadline-overdue';
    if (doc.days_until_deadline !== null && doc.days_until_deadline <= 3) return 'deadline-warning';
    return '';
  };

  if (loading && documents.length === 0) {
    return (
      <div className="doc-list-page">
        <div className="loading-spinner">{t('documents:misc.loadingDocuments')}</div>
      </div>
    );
  }

  return (
    <div className="doc-list-page">
      {/* Шапка */}
      <div className="doc-list-header">
        <div className="doc-list-header-left">
          <div>
            <h1>{t('documents:pageTitles.documentFlow')}</h1>
            <p className="subtitle">{t('documents:pageTitles.subtitle')}</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button className="btn-export" onClick={async () => {
            try { const r = await api.exportDocumentsExcel(); downloadBlob(r, 'Документы.xlsx'); } catch {}
          }}><Download size={16} /> {t('documents:misc.excelExport')}</button>
          {isAdmin && (
            <button className="btn-primary" onClick={() => navigate('/documents/create')}>
              <Plus size={18} /> {t('documents:pageTitles.createDocument')}
            </button>
          )}
        </div>
      </div>

      {/* Поиск и фильтры */}
      <div className="doc-list-toolbar">
        <form className="search-bar" onSubmit={handleSearch}>
          <Search size={18} />
          <input
            type="text"
            placeholder={t('documents:filters.searchPlaceholder')}
            value={filters.search}
            onChange={e => setFilters({ ...filters, search: e.target.value })}
          />
          <button type="submit" className="btn-search">{t('documents:filters.find')}</button>
        </form>
        <button
          className={`btn-filter ${showFilters ? 'active' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter size={18} /> {t('common:buttons.filter')} <ChevronDown size={14} />
        </button>
      </div>

      {showFilters && (
        <div className="filters-panel">
          <div className="filters-grid">
            <div className="filter-group">
              <label>{t('documents:filters.documentType')}</label>
              <select
                value={filters.document_type}
                onChange={e => setFilters({ ...filters, document_type: e.target.value })}
              >
                <option value="">{t('documents:filters.allTypes')}</option>
                {docTypes.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>{t('documents:filters.status')}</label>
              <select
                value={filters.status}
                onChange={e => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">{t('documents:filters.allStatuses')}</option>
                {statuses.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>{t('documents:filters.source')}</label>
              <select
                value={filters.authority}
                onChange={e => setFilters({ ...filters, authority: e.target.value })}
              >
                <option value="">{t('documents:filters.allSources')}</option>
                {authorities.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>{t('documents:filters.priority')}</label>
              <select
                value={filters.priority}
                onChange={e => setFilters({ ...filters, priority: e.target.value })}
              >
                <option value="">{t('documents:filters.allPriorities')}</option>
                {priorities.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>{t('documents:filters.responsibleDepartment')}</label>
              <select
                value={filters.responsible_department}
                onChange={e => setFilters({ ...filters, responsible_department: e.target.value })}
              >
                <option value="">{t('documents:filters.allDepartments')}</option>
                {departments.map(d => (
                  <option key={d.id} value={d.id}>{d.name_ru}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="filters-actions">
            <button className="btn-secondary" onClick={resetFilters}>{t('common:buttons.reset')}</button>
            <button className="btn-primary" onClick={fetchDocuments}>{t('common:buttons.apply')}</button>
          </div>
        </div>
      )}

      {/* Таблица документов */}
      {documents.length === 0 ? (
        <div className="empty-state">
          <FileText size={48} />
          <h3>{t('documents:misc.noDocuments')}</h3>
          <p>{t('documents:misc.documentsAppearAfterCreation')}</p>
        </div>
      ) : (
        <div className="doc-table-wrapper">
          <table className="doc-table">
            <thead>
              <tr>
                <th>{t('documents:table.regNumber')}</th>
                <th>{t('documents:table.subject')}</th>
                <th>{t('documents:table.type')}</th>
                <th>{t('documents:table.source')}</th>
                <th>{t('documents:table.priority')}</th>
                <th>{t('documents:table.deadline')}</th>
                <th>{t('documents:table.department')}</th>
                <th>{t('documents:table.status')}</th>
                <th>{t('documents:table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {documents.map(doc => (
                <tr key={doc.id} className={doc.is_overdue ? 'row-overdue' : ''}>
                  <td className="col-number">{doc.registration_number}</td>
                  <td className="col-title">
                    <span className="doc-title-text">{doc.title}</span>
                  </td>
                  <td>{doc.document_type_name}</td>
                  <td>{doc.authority_name || '—'}</td>
                  <td>
                    {doc.priority_name ? (
                      <span
                        className="priority-badge"
                        style={{ backgroundColor: doc.priority_color || '#94a3b8', color: '#fff' }}
                      >
                        {doc.priority_name}
                      </span>
                    ) : '—'}
                  </td>
                  <td className={getDeadlineClass(doc)}>
                    <div className="deadline-cell">
                      <Calendar size={14} />
                      <span>{formatDate(doc.deadline)}</span>
                      {doc.is_overdue && <AlertTriangle size={14} className="overdue-icon" />}
                      {!doc.is_overdue && doc.days_until_deadline !== null && doc.days_until_deadline <= 3 && (
                        <Clock size={14} className="warning-icon" />
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="department-tag">
                      <Building2 size={12} />
                      {doc.responsible_department_name}
                    </span>
                  </td>
                  <td>
                    <span
                      className="doc-status-badge"
                      style={{ backgroundColor: doc.status_color || '#94a3b8', color: '#fff' }}
                    >
                      {doc.status_name}
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn-view"
                      onClick={() => navigate(`/documents/${doc.id}`)}
                    >
                      {t('documents:misc.view')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default DocumentList;
